import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Plus,
  Trash2,
  Edit2,
  ThumbsUp,
  ThumbsDown,
  MinusCircle,
  Users,
  ListChecks,
  FileText,
  Clock,
  Search,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Link as LinkIcon,
  CheckSquare,
  Save,
  Check,
} from "lucide-react";
import {
  GovernanceMeeting,
  MeetingAgendaItem,
  MeetingParticipant,
  AvailableUser,
  AgendaVote,
  meetingAgendaService,
  meetingParticipantsService,
  agendaVotesService,
  meetingCloseService,
  governanceMeetingsService,
} from "../../services/governanceService";
import {
  Task,
  TaskLink,
  agendaLinksService,
  AgendaLink,
  meetingTasksService,
  tasksService,
} from "../../services/tasksService";
import {
  entityRegistryService,
  EntityType,
} from "../../services/entityRegistryService";
import EntityLinksMultiField from "../common/EntityLinksMultiField";
import CreateMeetingWizard from "./CreateMeetingWizard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import TaskList from "../tasks/TaskList";
import TaskForm from "../tasks/TaskForm";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Badge from "../common/Badge";
import FormControl from "../common/FormControl";
import MarkdownEditor from "../common/MarkdownEditor";
import Skeleton from "../common/Skeleton";
import { useAuth } from "../../contexts/AuthContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import { statusVariant, statusBadgeCss } from "../../utils/statusVariant";
import { confirmDelete } from "../common/confirm";
import { useFieldValidation } from "../../validation/useFieldValidation";
import {
  ValidatedInput,
  ValidatedTextarea,
} from "../../validation/ValidatedFields";

interface MeetingDetailModalProps {
  meeting: GovernanceMeeting;
  onClose: () => void;
  onMeetingUpdated: () => void;
  onEdit?: () => void;
  /**
   * 'chair' (default) renders the full editor surface (gated by canEdit which
   * additionally requires the current user to be the chair of an open meeting).
   * 'participant' force-suppresses every editor affordance regardless of who
   * is logged in — used by the staff portal (R15/R16, CONTEXT D5).
   * 'admin' (v2 R25) renders the modal as a read-only overview for the GRC
   * admin: NO edits, NO voting, NO close — but adds a status-overview header
   * and a per-agenda "Create Task" button on every row.
   */
  viewMode?: "admin" | "chair" | "participant";
  /**
   * Hard read-only override (History twin). When true it OVERRIDES viewMode
   * and forces a fully view-only surface: no add/edit/delete agenda, no
   * add/edit/delete task, no save minutes, no close meeting, no voting, no
   * Edit tab — everything observe-only. Defaults to false so every existing
   * caller behaves exactly as before.
   */
  readOnly?: boolean;
}

// CONTEXT D6: tab order Participants → Agenda → Tasks → Minutes → Edit.
// Minutes tab is the post-close render surface only; the Close action moved
// to the modal footer so it's accessible from any tab.
// Edit tab only shown when canEdit — renders the wizard inline.
type TabKey = "participants" | "agenda" | "tasks" | "minutes" | "edit";

const AGENDA_TYPE_OPTIONS = [
  { value: "Discussion", label: "Discussion" },
  { value: "Review", label: "Review" },
  { value: "Approval", label: "Approval" },
  { value: "Compliance", label: "Compliance" },
  { value: "Brainstorming", label: "Brainstorming" },
];

const formatTime = (t?: string | null) => {
  if (!t) return "";
  const parts = t.split(":");
  if (parts.length >= 2) {
    const h = parseInt(parts[0]);
    const m = parts[1];
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${ampm}`;
  }
  return t;
};

const MeetingDetailModal: React.FC<MeetingDetailModalProps> = ({
  meeting: meetingProp,
  onClose,
  onMeetingUpdated,
  onEdit,
  viewMode = "chair",
  readOnly = false,
}) => {
  const { state: authState } = useAuth();
  const { formatDate, formatDateTime } = useEntitySettings();
  const currentUserId = (authState.user as any)?.id ?? null;
  const [activeTab, setActiveTab] = useState<TabKey>(
    viewMode === "participant" ? "participants" : "edit",
  );
  const [isVisible, setIsVisible] = useState(true);

  // Footer slot for the inline edit wizard. The wizard portals its action row
  // (Back / Cancel / Next / Update) into this node so those buttons render in
  // the modal's real footer bar instead of scrolling inside the body. Held in
  // state (not a plain ref) so the wizard re-renders and fills the portal once
  // the node mounts.
  const [wizardFooterSlot, setWizardFooterSlot] = useState<HTMLDivElement | null>(
    null,
  );

  // Agenda state
  const [agendaItems, setAgendaItems] = useState<MeetingAgendaItem[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(true);
  const [showAgendaForm, setShowAgendaForm] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState<MeetingAgendaItem | null>(
    null,
  );
  const [agendaForm, setAgendaForm] = useState<Partial<MeetingAgendaItem>>({});
  const agendaVh = useFieldValidation();
  const [submitting, setSubmitting] = useState(false);
  const [expandedAgenda, setExpandedAgenda] = useState<number | null>(null);
  const [agendaVotes, setAgendaVotes] = useState<Record<number, AgendaVote[]>>(
    {},
  );
  // Vote modal: clicking up/down opens this to (optionally) attach a remark
  // before the vote is cast. null = closed. `remark` is the in-progress text.
  const [voteModal, setVoteModal] = useState<{
    agendaId: number;
    agendaName: string;
    vote: "FOR" | "AGAINST" | "ABSTAIN" | "UPVOTE" | "DOWNVOTE";
    remark: string;
  } | null>(null);
  const [voteSubmitting, setVoteSubmitting] = useState(false);

  // Participants state
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserPicker, setShowUserPicker] = useState(false);

  // Minutes / Close state. T9.8: after close we still need to surface the
  // freshly composed minutes_text + new meeting_status without remounting
  // the modal, so we hold a local override of the prop. The prop is the
  // initial value; the close handler refreshes from the API response.
  const [closing, setClosing] = useState(false);
  const [meetingState, setMeetingState] =
    useState<GovernanceMeeting>(meetingProp);
  // Keep local state in sync when the parent passes a different meeting
  // (e.g. row click switches between two meetings within the same list view).
  useEffect(() => {
    setMeetingState(meetingProp);
  }, [meetingProp]);
  // Alias so the rest of the render keeps the short `meeting` name and the
  // overridden status / minutes_text flow through every downstream check.
  const meeting = meetingState;

  // Manual minutes (migration 159): a human-written record editable by the
  // chair or any attended participant until the chair finalizes it. The draft
  // is seeded from the meeting and re-seeded only when the parent swaps the
  // meeting — local edits survive a save (we hold the response in meetingState).
  const [manualMinutesDraft, setManualMinutesDraft] = useState<string>(
    meetingProp.manual_minutes_text || "",
  );
  const [savingMinutes, setSavingMinutes] = useState(false);
  // Transient "Saved" confirmation — without it a successful save is
  // indistinguishable from a silent failure (UAT 2026-07-10, staff persona).
  const [minutesJustSaved, setMinutesJustSaved] = useState(false);
  const [generatingMinutes, setGeneratingMinutes] = useState(false);
  const [autoGenOpen, setAutoGenOpen] = useState(false);
  useEffect(() => {
    setManualMinutesDraft(meetingProp.manual_minutes_text || "");
  }, [meetingProp]);

  // v3 attend flow — staff portal must explicitly opt-in before voting.
  // Seed from the per-row `my_has_attended` field projected by /meetings/mine.
  const [attended, setAttended] = useState<boolean>(
    Boolean((meetingProp as any)?.my_has_attended),
  );
  const [attendedFromTime, setAttendedFromTime] = useState<string | undefined>(
    (meetingProp as any)?.my_attended_from_time,
  );
  const [attendBusy, setAttendBusy] = useState(false);
  useEffect(() => {
    setAttended(Boolean((meetingProp as any)?.my_has_attended));
    setAttendedFromTime((meetingProp as any)?.my_attended_from_time);
  }, [meetingProp]);

  const handleAttend = async () => {
    if (attendBusy || attended) return;
    setAttendBusy(true);
    try {
      const result = await governanceMeetingsService.attend(meeting.id);
      setAttended(Boolean(result.has_attended));
      setAttendedFromTime(result.attended_from_time);
      setError(null);
      // Refresh the parent list so meeting_status (DRAFT/SCHEDULED -> IN_PROGRESS)
      // and the my_has_attended flag are reflected outside this modal.
      onMeetingUpdated();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || "Failed to mark attendance",
      );
    } finally {
      setAttendBusy(false);
    }
  };
  // minutesText kept for back-compat with the closeMeeting API which still
  // accepts a text payload; server ignores it now (T5 auto-composes) so we
  // pass the empty string and read meeting.minutes_text after the response.
  const minutesText = "";

  // Tasks tab state
  const [meetingTasks, setMeetingTasks] = useState<Task[]>([]);
  const [meetingTasksLoading, setMeetingTasksLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskFormForAgendaId, setTaskFormForAgendaId] = useState<number | null>(
    null,
  );
  // T9.6: seed the TaskForm with the agenda's description + linked entities
  // when launching from an agenda's Create Task button (SPEC R13/R14). Shaped
  // as a partial Task so TaskForm's initial-prop pathway lights up.
  const [taskFormSeed, setTaskFormSeed] = useState<Task | null>(null);

  // Per-agenda polymorphic links (loaded lazily on expand)
  const [agendaLinks, setAgendaLinks] = useState<Record<number, AgendaLink[]>>(
    {},
  );

  // Per-agenda tasks list (loaded lazily on expand)
  const [agendaTasks, setAgendaTasks] = useState<Record<number, Task[]>>({});

  // Registry id for AGENDA_ITEM
  const [agendaTypeId, setAgendaTypeId] = useState<number | null>(null);

  // Registry id for MEETING — used to stamp a meeting-level task's source as
  // (MEETING, meeting.id) so it surfaces in the Meeting → Tasks tab. The
  // backend list_meeting_tasks matches source_type=MEETING AND
  // source_record_id=meeting.id; without this a meeting-level task is saved
  // with a NULL source and is invisible in that tab.
  const [meetingTypeId, setMeetingTypeId] = useState<number | null>(null);

  // Linkable types loaded from registry. Filtered list (MEETING excluded per
  // SPEC R7 / risk register: you cannot link a meeting from inside another
  // meeting's agenda) feeds the EntityLinksMultiField pickers (T9.3).
  const [linkableTypeCodes, setLinkableTypeCodes] = useState<string[]>([]);

  // Draft link set for the create-agenda form. Sent transactionally inside the
  // POST /meeting-agenda-items body (T9.4 wires the request shape).
  const [newAgendaLinks, setNewAgendaLinks] = useState<TaskLink[]>([]);

  // v2 R24: collapsible "Add links (optional)" disclosure per agenda row.
  // Map<agendaId, boolean> for the inline-expand link manager; a single
  // boolean for the create-agenda form (it's only ever one form at a time).
  const [newAgendaLinksOpen, setNewAgendaLinksOpen] = useState(false);

  // Error
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 220);
  }, [onClose]);

  // If the edit tab is active but the meeting becomes non-editable (e.g.
  // just closed), fall back to participants so the body isn't blank.
  useEffect(() => {
    const status = meeting.meeting_status;
    if (activeTab === "edit" && (status === "CLOSED" || status === "CANCELLED")) {
      setActiveTab("participants");
    }
  }, [activeTab, meeting.meeting_status]);

  useEffect(() => {
    // The agenda link/type pickers these ids feed are not shown to admin
    // (admin only sees the wizard, which loads its own type registry).
    if (viewMode === "admin") return;
    entityRegistryService.listTypes(true).then((types: EntityType[]) => {
      const t = types.find((x) => x.type_code === "AGENDA_ITEM");
      if (t) setAgendaTypeId(t.id);
      const m = types.find((x) => x.type_code === "MEETING");
      if (m) setMeetingTypeId(m.id);
      // Per SPEC R7 + risk register: exclude MEETING so users cannot link a
      // meeting to itself / another meeting via an agenda item. Also exclude
      // AGENDA_ITEM (agenda → agenda links make no sense in this UI).
      setLinkableTypeCodes(
        types
          .filter(
            (x) => x.type_code !== "MEETING" && x.type_code !== "AGENDA_ITEM",
          )
          .map((x) => x.type_code),
      );
    });
  }, [viewMode]);

  // Load agenda items
  const loadAgenda = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      // Only paint the skeleton on the first/explicit load. Background refreshes
      // after a vote/remark pass silent:true so the expanded accordion updates
      // in place instead of being torn down and re-mounted (the "blink").
      if (!opts?.silent) setAgendaLoading(true);
      const data = await meetingAgendaService.getByMeeting(meeting.id);
      setAgendaItems(data);
    } catch (err: any) {
      console.error("Error loading agenda:", err);
    } finally {
      if (!opts?.silent) setAgendaLoading(false);
    }
  }, [meeting.id]);

  // Load participants
  const loadParticipants = useCallback(async () => {
    try {
      setParticipantsLoading(true);
      const data = await meetingParticipantsService.getByMeeting(meeting.id);
      setParticipants(data);
    } catch (err: any) {
      console.error("Error loading participants:", err);
    } finally {
      setParticipantsLoading(false);
    }
  }, [meeting.id]);

  useEffect(() => {
    // GRC Team (admin) sees ONLY the embedded Create/Edit wizard, which loads
    // its own participants + agenda. Skip these eager fetches for admin so we
    // don't load (and re-render) data that is never shown.
    if (viewMode === "admin") return;
    loadAgenda();
    loadParticipants();
  }, [loadAgenda, loadParticipants, viewMode]);

  // Load available users for picker
  const loadAvailableUsers = useCallback(
    async (search?: string) => {
      try {
        const data = await meetingParticipantsService.getAvailableUsers(
          meeting.id,
          search,
        );
        setAvailableUsers(data);
      } catch (err: any) {
        console.error("Error loading available users:", err);
      }
    },
    [meeting.id],
  );

  useEffect(() => {
    if (showUserPicker) loadAvailableUsers(userSearch || undefined);
  }, [showUserPicker, userSearch, loadAvailableUsers]);

  // Load votes for an agenda item
  const loadVotesForAgenda = async (agendaId: number) => {
    try {
      const data = await agendaVotesService.getByAgenda(agendaId);
      setAgendaVotes((prev) => ({ ...prev, [agendaId]: data }));
    } catch (err: any) {
      console.error("Error loading votes:", err);
    }
  };

  // --- Agenda handlers ---
  const handleAddAgenda = () => {
    setEditingAgenda(null);
    setAgendaForm({
      agenda_name: "",
      agenda_description: "",
      agenda_type: "Discussion",
      presenter: "",
      duration_minutes: 15,
      display_order: agendaItems.length + 1,
    });
    setNewAgendaLinks([]);
    agendaVh.reset();
    setShowAgendaForm(true);
  };

  const handleEditAgenda = (item: MeetingAgendaItem) => {
    setEditingAgenda(item);
    setAgendaForm({ ...item });
    agendaVh.reset();
    setShowAgendaForm(true);
  };

  const handleSaveAgenda = async () => {
    const specs = [
      { name: "agenda_name", code: 109, value: agendaForm.agenda_name, required: true },
      { name: "presenter", code: 109, value: agendaForm.presenter },
      {
        name: "duration_minutes",
        code: 206,
        value:
          agendaForm.duration_minutes == null
            ? undefined
            : String(agendaForm.duration_minutes),
      },
      { name: "agenda_description", code: 110, value: agendaForm.agenda_description },
    ];
    if (editingAgenda) {
      specs.push({ name: "resolution_text", code: 110, value: agendaForm.resolution_text });
    }
    if (!agendaVh.validateAll(specs)) return;
    try {
      setSubmitting(true);
      setError(null);
      if (editingAgenda) {
        await meetingAgendaService.update(editingAgenda.id, agendaForm);
      } else {
        // SPEC R8: agenda row + its initial link set persisted in ONE
        // transactional POST. Backend wraps the insert + N link inserts in
        // a single `async with conn.transaction()` block.
        await meetingAgendaService.create({
          ...agendaForm,
          meeting_id: meeting.id,
          links: newAgendaLinks.map((l) => ({
            item_type_id: l.type_id,
            item_record_id: l.record_id,
            relationship_type: "Discuss",
          })),
        });
        setNewAgendaLinks([]);
      }
      setShowAgendaForm(false);
      loadAgenda();
    } catch (err: any) {
      setError(err.message || "Failed to save agenda item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgenda = async (id: number) => {
    if (!(await confirmDelete({ title: "Delete Agenda Item?", text: "This action cannot be undone." }))) return;
    try {
      await meetingAgendaService.delete(id);
      loadAgenda();
    } catch (err: any) {
      setError(err.message || "Failed to delete agenda item");
    }
  };

  const loadLinksForAgenda = useCallback(
    async (agendaId: number) => {
      try {
        const data = await agendaLinksService.list(meeting.id, agendaId);
        setAgendaLinks((prev) => ({ ...prev, [agendaId]: data }));
      } catch (err: any) {
        console.error("Error loading agenda links:", err);
      }
    },
    [meeting.id],
  );

  const loadTasksForAgenda = useCallback(
    async (agendaId: number) => {
      if (!agendaTypeId) return;
      try {
        const data = await tasksService.list({
          source_type_code: "AGENDA_ITEM",
          source_record_id: agendaId,
        });
        setAgendaTasks((prev) => ({ ...prev, [agendaId]: data }));
      } catch (err: any) {
        console.error("Error loading agenda tasks:", err);
      }
    },
    [agendaTypeId],
  );

  const handleToggleAgendaExpand = (agendaId: number) => {
    if (expandedAgenda === agendaId) {
      setExpandedAgenda(null);
    } else {
      setExpandedAgenda(agendaId);
      loadVotesForAgenda(agendaId);
      loadLinksForAgenda(agendaId);
      loadTasksForAgenda(agendaId);
    }
  };

  // --- Agenda link handlers ---
  // Multi-field flow: diff TaskLink[] against the loaded AgendaLink[] and
  // POST creates + DELETE removes. Re-load to sync server-resolved labels.
  const handleAgendaLinksChange = async (
    agendaId: number,
    next: TaskLink[],
  ) => {
    const prev = agendaLinks[agendaId] || [];
    // New = items in `next` without an id (not yet persisted).
    const additions = next.filter((l) => !l.id);
    // Removed = items in `prev` whose id is not present in `next`.
    const nextIds = new Set(next.filter((l) => l.id).map((l) => l.id));
    const removals = prev.filter((l) => !nextIds.has(l.id));
    try {
      for (const add of additions) {
        await agendaLinksService.create(meeting.id, agendaId, {
          item_type_id: add.type_id,
          item_record_id: add.record_id,
          relationship_type: "Discuss",
        });
      }
      for (const rem of removals) {
        await agendaLinksService.remove(rem.id);
      }
      loadLinksForAgenda(agendaId);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to update links",
      );
      // Always re-sync from server so the UI never lies.
      loadLinksForAgenda(agendaId);
    }
  };

  // --- Meeting tasks handlers ---
  const loadMeetingTasks = useCallback(async () => {
    try {
      setMeetingTasksLoading(true);
      const data = await meetingTasksService.list(meeting.id);
      setMeetingTasks(data);
    } catch (err: any) {
      console.error("Error loading meeting tasks:", err);
    } finally {
      setMeetingTasksLoading(false);
    }
  }, [meeting.id]);

  useEffect(() => {
    // Load tasks only when the Tasks tab is actually open. (The old admin
    // overview header that needed the tasks count up front was removed, so
    // admin no longer eager-loads tasks.)
    if (activeTab === "tasks") loadMeetingTasks();
  }, [activeTab, loadMeetingTasks]);

  const handleNewTaskFromAgenda = (agenda: MeetingAgendaItem) => {
    setEditingTask(null);
    setTaskFormForAgendaId(agenda.id);
    // Build the seed (SPEC R13/R14): description inherits agenda_name +
    // agenda_description + resolution_text; linked_entities inherits the
    // agenda's GOV_agenda_links transformed to TaskLink shape. The seed
    // is fed to TaskForm via its `initial` prop so its existing init
    // pathways (description, linked_entities) light up unchanged.
    const inheritedLinks = (agendaLinks[agenda.id] || []).map((l) => ({
      type_id: l.item_type_id,
      type_code: l.item_type_code || undefined,
      record_id: l.item_record_id,
      label: l.item_label || undefined,
      code: l.item_code || undefined,
    }));
    // Always link the spawned task back to its originating AGENDA_ITEM so
    // the agenda surfaces in task detail views and reverse-link queries
    // (in addition to the polymorphic links the agenda already carries).
    const agendaSelfLink =
      agendaTypeId !== null
        ? [{
            type_id: agendaTypeId,
            type_code: "AGENDA_ITEM",
            record_id: agenda.id,
            label: agenda.agenda_name,
            code: undefined,
          }]
        : [];
    const links = [...agendaSelfLink, ...inheritedLinks];
    const descParts = [
      agenda.agenda_name,
      agenda.agenda_description || "",
      agenda.resolution_text || "",
    ].filter((p) => p && p.trim().length > 0);
    setTaskFormSeed({
      id: 0,
      entity_id: meeting.entity_id,
      name: "",
      description: descParts.join("\n\n"),
      grace_period_days: 0,
      status: "OPEN",
      feedback_required: false,
      effectiveness_required: false,
      is_active: 1,
      linked_entities: links,
    } as Task);
    setShowTaskForm(true);
  };

  const handleNewTaskFromMeeting = () => {
    setEditingTask(null);
    setTaskFormForAgendaId(null);
    setTaskFormSeed(null);
    setShowTaskForm(true);
  };

  const handleTaskSaved = (saved: Task) => {
    setShowTaskForm(false);
    setEditingTask(null);
    setTaskFormSeed(null);
    if (taskFormForAgendaId) {
      loadTasksForAgenda(taskFormForAgendaId);
    }
    loadMeetingTasks();
    setTaskFormForAgendaId(null);
  };

  const handleTaskEdit = (t: Task) => {
    setEditingTask(t);
    setTaskFormForAgendaId(t.source_record_id ?? null);
    setShowTaskForm(true);
  };

  const handleTaskDelete = async (t: Task) => {
    if (!(await confirmDelete({ title: "Delete Task?", text: `Delete task "${t.name}"? This action cannot be undone.` }))) return;
    try {
      await tasksService.remove(t.id);
      if (t.source_record_id) loadTasksForAgenda(t.source_record_id);
      loadMeetingTasks();
    } catch (err: any) {
      setError(err?.message || "Failed to delete task");
    }
  };

  // --- Vote handlers ---
  // SPEC R22: BINARY meetings accept UPVOTE/DOWNVOTE only; TRINARY meetings
  // accept FOR/AGAINST/ABSTAIN only. Server enforces; UI just routes the
  // right values per vote_model.
  const handleVote = async (
    agendaId: number,
    vote: "FOR" | "AGAINST" | "ABSTAIN" | "UPVOTE" | "DOWNVOTE",
    remarks?: string,
  ) => {
    try {
      await agendaVotesService.castVote({
        meeting_id: meeting.id,
        agenda_instance_id: agendaId,
        vote,
        remarks: remarks?.trim() || undefined,
      });
      // silent:true → refresh tallies/status in place, no skeleton "blink".
      loadVotesForAgenda(agendaId);
      loadAgenda({ silent: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || "Failed to cast vote");
    }
  };

  // Open the vote modal for a direction, pre-filling any remark already on the
  // current user's existing vote so they can edit rather than retype it.
  const openVoteModal = (
    item: MeetingAgendaItem,
    vote: "FOR" | "AGAINST" | "ABSTAIN" | "UPVOTE" | "DOWNVOTE",
  ) => {
    const mine = (agendaVotes[item.id] || []).find(
      (v) => v.user_id === currentUserId,
    );
    setVoteModal({
      agendaId: item.id,
      agendaName: item.agenda_name || `Agenda #${item.id}`,
      vote,
      remark: mine?.remarks ?? "",
    });
  };

  const submitVoteModal = async () => {
    if (!voteModal) return;
    setVoteSubmitting(true);
    try {
      await handleVote(voteModal.agendaId, voteModal.vote, voteModal.remark);
      setVoteModal(null);
    } finally {
      setVoteSubmitting(false);
    }
  };

  // --- Participant handlers ---
  const handleAddParticipant = async (userId: number) => {
    try {
      await meetingParticipantsService.add({
        meeting_id: meeting.id,
        user_id: userId,
      });
      loadParticipants();
      loadAvailableUsers(userSearch || undefined);
    } catch (err: any) {
      setError(err.message || "Failed to add participant");
    }
  };

  const handleRemoveParticipant = async (id: number) => {
    if (!(await confirmDelete({ title: "Remove Participant?", text: "Remove this participant from the meeting?", confirmText: "Yes, Remove" }))) return;
    try {
      await meetingParticipantsService.remove(id);
      loadParticipants();
      loadAvailableUsers(userSearch || undefined);
    } catch (err: any) {
      setError(err.message || "Failed to remove participant");
    }
  };

  // --- Close meeting ---
  // T9.8: server auto-composes minutes (T5). After close we keep the modal
  // open, refresh the local meeting from the API response so the new status
  // + minutes_text propagate, switch to the Minutes tab so the user sees the
  // generated document, and still call onMeetingUpdated() so the parent list
  // view re-fetches in the background.
  const handleCloseMeeting = async () => {
    if (
      !(await confirmDelete({
        title: "Close Meeting?",
        text: "Minutes will be auto-generated and the meeting will be marked CLOSED.",
        confirmText: "Yes, Close",
      }))
    )
      return;
    try {
      setClosing(true);
      const updated = await meetingCloseService.closeMeeting(
        meeting.id,
        minutesText,
      );
      setMeetingState(updated);
      onMeetingUpdated();
      setActiveTab("minutes");
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to close meeting",
      );
    } finally {
      setClosing(false);
    }
  };

  // --- Manual minutes handlers (migration 159) ---
  const handleSaveMinutes = async () => {
    try {
      setSavingMinutes(true);
      const updated = await meetingCloseService.saveManualMinutes(
        meeting.id,
        manualMinutesDraft,
      );
      setMeetingState(updated);
      onMeetingUpdated();
      setError(null);
      setMinutesJustSaved(true);
      window.setTimeout(() => setMinutesJustSaved(false), 2500);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || "Failed to save minutes",
      );
    } finally {
      setSavingMinutes(false);
    }
  };

  const handleFinalizeMinutes = async () => {
    if (
      !(await confirmDelete({
        title: "Finalize Minutes?",
        text: "This locks the minutes read-only. You can reopen them later if needed.",
        confirmText: "Yes, Finalize",
      }))
    )
      return;
    try {
      setSavingMinutes(true);
      // Persist any unsaved edits first — the backend rejects finalizing empty
      // minutes, and we want the locked copy to match what's on screen.
      const saved = await meetingCloseService.saveManualMinutes(
        meeting.id,
        manualMinutesDraft,
      );
      setMeetingState(saved);
      const updated = await meetingCloseService.finalizeManualMinutes(meeting.id);
      setMeetingState(updated);
      onMeetingUpdated();
      setError(null);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to finalize minutes",
      );
    } finally {
      setSavingMinutes(false);
    }
  };

  const handleUnfinalizeMinutes = async () => {
    if (
      !(await confirmDelete({
        title: "Reopen Minutes?",
        text: "This unlocks the minutes so they can be edited again.",
        confirmText: "Yes, Reopen",
      }))
    )
      return;
    try {
      setSavingMinutes(true);
      const updated = await meetingCloseService.unfinalizeManualMinutes(
        meeting.id,
      );
      setMeetingState(updated);
      setManualMinutesDraft(updated.manual_minutes_text || "");
      onMeetingUpdated();
      setError(null);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to reopen minutes",
      );
    } finally {
      setSavingMinutes(false);
    }
  };

  // Compose the auto-generated minutes on demand (agenda items + per-person
  // responses + spawned tasks) and append them to the editable draft. Works
  // before AND after close — the server composes fresh each call, so the
  // narrative reflects the latest votes/tasks. Replaces the old post-close-only
  // "insert stored summary" path.
  const handleGenerateAutoMinutes = async () => {
    try {
      setGeneratingMinutes(true);
      const text = await meetingCloseService.generateAutoMinutes(meeting.id);
      setManualMinutesDraft((prev) =>
        prev && prev.trim() ? `${prev}\n\n${text}` : text,
      );
      setError(null);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to generate minutes",
      );
    } finally {
      setGeneratingMinutes(false);
    }
  };

  const isClosed = meeting.meeting_status === "CLOSED";
  const isCancelled = meeting.meeting_status === "CANCELLED";

  // R11/R12: voting is enabled for any user listed in the meeting's
  // participants table (chair is auto-inserted by T2 backend, so chair
  // votes too). Vote buttons disable when the meeting is closed.
  // v2 R25: admin viewMode never votes (admin observes; only participants vote).
  const isParticipant = currentUserId != null &&
    participants.some((p) => p.user_id === currentUserId);
  // v3: voting requires the explicit Attend click for participants. Admin
  // viewMode never votes (read-only). Chair is auto-marked attended via the
  // same opt-in flow (chair also clicks Mark as Attending).
  const canVote =
    !readOnly && viewMode !== "admin" && !isClosed && !isCancelled && isParticipant && attended;

  // Lookup helper: current user's already-cast vote for a given agenda (used
  // to highlight the selected button so the UPSERT semantics are visible).
  const currentUserVoteFor = (agendaId: number): string | null => {
    if (currentUserId == null) return null;
    const votes = agendaVotes[agendaId];
    if (!votes) return null;
    const mine = votes.find((v) => v.user_id === currentUserId);
    return mine ? mine.vote : null;
  };
  // canEdit gate.
  //   - chair viewMode + open meeting + current user IS the chair → full edit
  //   - admin viewMode + open meeting → full edit (GRC team owns the meeting
  //     they created; they edit agenda/participants/etc just like chair, but
  //     still NEVER see voting controls — that's `canVote`'s job).
  //   - participant viewMode → never edits.
  // UAT 2026-05-17 superseded the v2 R25 "admin = strictly read-only" wording.
  const canEdit =
    !readOnly &&
    !isClosed &&
    !isCancelled &&
    (
      viewMode === "admin" ||
      (viewMode === "chair" &&
        currentUserId != null &&
        currentUserId === meeting.chairman_id)
    );

  // --- Manual minutes gates (migration 159) ---
  // Edit: chair OR a participant who has marked attendance, while not finalized
  // AND the meeting is not yet closed. Once the meeting is closed the minutes
  // are locked read-only (the auto-generated summary + manual text stay visible
  // for reference, but no edit / save / generate affordances are shown).
  // Finalize / unfinalize: chair only.
  const isChair = currentUserId != null && currentUserId === meeting.chairman_id;
  const myParticipant = participants.find((p) => p.user_id === currentUserId);
  const myAttended = attended || Boolean(myParticipant?.has_attended);

  // Staff portal opens this modal with viewMode='participant' for everyone —
  // including the chairman. canEdit stays false there (no edit affordances),
  // but the chair must still be able to close their own meeting, so Close
  // gets its own gate. Backend allows chair OR GOV_MEETINGS edit holder.
  const canCloseMeeting =
    !readOnly && !isClosed && !isCancelled && (canEdit || isChair);
  const minutesFinalized = Boolean(meeting.manual_minutes_finalized);
  const canEditMinutes =
    !readOnly && !isClosed && !minutesFinalized && (isChair || myAttended);
  const canFinalizeMinutes =
    !readOnly && isChair && !minutesFinalized && manualMinutesDraft.trim().length > 0;
  const canUnfinalizeMinutes = !readOnly && isChair && minutesFinalized;
  // Finalizer is always the chair (chair-only finalize), so resolve the name
  // from the participants list with chairman_name as fallback.
  const minutesFinalizerName = (() => {
    const fid = meeting.manual_minutes_finalized_by;
    if (!fid) return "";
    const p = participants.find((pp) => pp.user_id === fid);
    return p?.user_name || meeting.chairman_name || `User #${fid}`;
  })();

  // v2 R25: Per-agenda "Create Task" button visibility rule:
  //   - admin:       shown on EVERY row regardless of agenda_status
  //   - participant: shown on EVERY row regardless of agenda_status
  //   - chair:       shown only on PASSED rows (v1 R13 behavior preserved)
  const canCreateTaskForAgenda = (item: MeetingAgendaItem): boolean => {
    // Hard read-only override: never offer task creation.
    if (readOnly) return false;
    if (viewMode === "admin" || viewMode === "participant") return true;
    // chair branch — keep v1 behavior (canEdit + PASSED).
    return canEdit && item.agenda_status === "PASSED";
  };

  return (
    <Modal isOpen={isVisible} onClose={handleClose} size="xl">
      {/* Custom header with meeting info */}
      <Modal.Header
        actions={
          <>
            {/* v3 staff Attend button — only for participant viewMode while the
                meeting is open and the user has not yet opted in. */}
            {!readOnly && viewMode === "participant" && isParticipant && !attended && !isClosed && !isCancelled && (
              <Button
                variant="fill"
                icon={Check}
                onClick={handleAttend}
                disabled={attendBusy}
                data-testid="BTN_meeting_detail_attend"
              >
                {attendBusy ? "Marking…" : "Mark as Attending"}
              </Button>
            )}
            {/* Subtle attendance confirmation pill (participant viewMode only). */}
            {viewMode === "participant" && attended && (
              <span className={`${statusBadgeCss('active')} meeting-detail-attended-pill`}>
                <Check size={12} /> Attended{attendedFromTime ? ` at ${formatTime(attendedFromTime)}` : ""}
              </span>
            )}
            {/* Close Meeting lives in the header (CONTEXT D6) so it is reachable
                from any tab. Shown to the chair in ANY viewMode (staff portal
                passes 'participant' even for the chairman) and to the GRC Team
                (admin viewMode) — the backend gate is chair OR GOV_MEETINGS
                edit, mirroring update/delete. */}
            {canCloseMeeting && (
              <Button
                variant="delete-outline"
                onClick={handleCloseMeeting}
                disabled={closing}
                data-testid="BTN_meeting_detail_close_meeting"
              >
                {closing ? "Closing..." : "Close Meeting"}
              </Button>
            )}
          </>
        }
      >
        <div className="meeting-detail-header-stack">
          {/* Row 1 — title is the dominant element; status pill rides alongside.
              Right-side actions (Attend / pill / Edit / X) live in the Header
              `actions` prop, so this row contains only the primary identity. */}
          <div className="meeting-detail-header-primary">
            <span className="meeting-detail-title">
              {viewMode === "participant"
                ? `Attending: ${meeting.meeting_title}`
                : meeting.meeting_title}
            </span>
            <Badge variant={statusVariant(meeting.meeting_status)} size="sm">
              {(meeting.meeting_status || "").replace("_", " ")}
            </Badge>
          </div>
          {/* Row 2 — supporting metadata, muted and small. Dot-separated so the
              eye reads it as one calm line rather than four competing fields. */}
          <div className="meeting-detail-header-meta">
            <span className="meeting-detail-meta-code">
              {meeting.meeting_code}
            </span>
            <span className="meeting-detail-meta-sep">·</span>
            <span>{meeting.meeting_type}</span>
            {meeting.scheduled_date && (
              <>
                <span className="meeting-detail-meta-sep">·</span>
                <span>
                  {formatDate(meeting.scheduled_date)}
                </span>
              </>
            )}
            <span className="meeting-detail-meta-sep">·</span>
            <span className="meeting-detail-meta-time">
              <Clock size={11} className="meeting-detail-meta-icon" />{" "}
              {formatTime(meeting.scheduled_time_from)} –{" "}
              {formatTime(meeting.scheduled_time_to)}
            </span>
            {meeting.location && (
              <>
                <span className="meeting-detail-meta-sep">·</span>
                <span>{meeting.location}</span>
              </>
            )}
          </div>
        </div>
      </Modal.Header>

      {/* Tab bar — direct flex child of modal card, stays sticky above scrollable body.
          Hidden for the GRC Team (admin viewMode): they only add/edit a meeting via
          the wizard stepper, so the Participants/Agenda/Tasks/Minutes tabs are noise.
          activeTab inits to "edit" for admin, so the wizard renders on its own. The
          tabs remain for chair + participant (staff persona) where attending the
          meeting needs them. */}
      {viewMode !== "admin" && (
      <div className="app-modal-tabs meeting-detail-tabs-strip">
        {[
          ...(canEdit
            ? [
                {
                  key: "edit" as TabKey,
                  label: "Basic Details",
                  icon: <Edit2 size={14} />,
                },
              ]
            : []),
          {
            key: "participants" as TabKey,
            label: "Participants",
            icon: <Users size={14} />,
            count: participants.length,
          },
          {
            key: "agenda" as TabKey,
            label: "Agenda",
            icon: <ListChecks size={14} />,
            count: agendaItems.length,
          },
          {
            key: "tasks" as TabKey,
            label: "Tasks",
            icon: <CheckSquare size={14} />,
            count: meetingTasks.length > 0 ? meetingTasks.length : undefined,
          },
          {
            key: "minutes" as TabKey,
            label: "Minutes",
            icon: <FileText size={14} />,
          },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`app-modal-tab${activeTab === tab.key ? " app-modal-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`TAB_meeting_detail_${tab.key}`}
          >
            {tab.icon} {tab.label}
            {tab.count !== undefined && (
              <span className="app-modal-tab-badge">{tab.count}</span>
            )}
          </button>
        ))}
      </div>
      )}

      {/* Scrollable tab content */}
      <Modal.Body>
        {/* Error banner — dismissable; auto-clears on next successful action */}
        {error && (
          <div className="meeting-detail-error-banner" role="alert">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="meeting-detail-error-banner__close"
              aria-label="Dismiss error"
              data-testid="BTN_meeting_detail_error_dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* === AGENDA TAB === */}
        {activeTab === "agenda" && (
          <div>
            {/* FIX 1: when the create/edit form is open, render ONLY the form.
                Hides the section header, empty-state, and list so the user
                isn't confused by a form appearing below the "No agenda items
                yet" message (UAT regression). */}
            {showAgendaForm ? (
              <div className="meeting-detail-inline-panel">
                <h4 className="meeting-detail-inline-panel__title">
                  {editingAgenda ? "Edit Agenda Item" : "Create Agenda Item"}
                </h4>
                <div className="meeting-detail-form-grid">
                  <ValidatedInput
                    vh={agendaVh}
                    name="agenda_name"
                    code={109}
                    required
                    label="Item Name"
                    value={agendaForm.agenda_name || ""}
                    onChange={(e) =>
                      setAgendaForm((p) => ({
                        ...p,
                        agenda_name: e.target.value,
                      }))
                    }
                    placeholder="Agenda item name"
                    data-testid="INP_meeting_detail_agenda_name"
                  />
                  <FormControl.SearchSelect
                    label="Type"
                    value={agendaForm.agenda_type || "Discussion"}
                    onChange={(value) =>
                      setAgendaForm((p) => ({
                        ...p,
                        agenda_type: String(value ?? "Discussion"),
                      }))
                    }
                    options={AGENDA_TYPE_OPTIONS}
                    data-testid="SEL_meeting_detail_agenda_type"
                  />
                  <ValidatedInput
                    vh={agendaVh}
                    name="presenter"
                    code={109}
                    label="Presenter"
                    value={agendaForm.presenter || ""}
                    onChange={(e) =>
                      setAgendaForm((p) => ({
                        ...p,
                        presenter: e.target.value,
                      }))
                    }
                    placeholder="Presenter name"
                    data-testid="INP_meeting_detail_agenda_presenter"
                  />
                  <ValidatedInput
                    vh={agendaVh}
                    name="duration_minutes"
                    code={206}
                    label="Duration (min)"
                    type="number"
                    min={0}
                    value={String(agendaForm.duration_minutes || 15)}
                    onChange={(e) =>
                      setAgendaForm((p) => ({
                        ...p,
                        duration_minutes: parseInt(e.target.value) || 15,
                      }))
                    }
                    data-testid="INP_meeting_detail_agenda_duration"
                  />
                  <div className="meeting-detail-form-grid__full">
                    <ValidatedTextarea
                      vh={agendaVh}
                      name="agenda_description"
                      code={110}
                      label="Description"
                      rows={2}
                      value={agendaForm.agenda_description || ""}
                      onChange={(e) =>
                        setAgendaForm((p) => ({
                          ...p,
                          agenda_description: e.target.value,
                        }))
                      }
                      placeholder="Brief description"
                      data-testid="INP_meeting_detail_agenda_description"
                    />
                  </div>
                  {editingAgenda && (
                    <div className="meeting-detail-form-grid__full">
                      <ValidatedTextarea
                        vh={agendaVh}
                        name="resolution_text"
                        code={110}
                        label="Resolution Text"
                        rows={2}
                        value={agendaForm.resolution_text || ""}
                        onChange={(e) =>
                          setAgendaForm((p) => ({
                            ...p,
                            resolution_text: e.target.value,
                          }))
                        }
                        placeholder="Resolution / outcome for this agenda item"
                        data-testid="INP_meeting_detail_agenda_resolution"
                      />
                    </div>
                  )}
                  {/* Links picker — only shown on CREATE (transactional path).
                      For EDIT we keep the inline per-row link manager so users
                      can add/remove links on already-saved agendas. SPEC R7.
                      v2 R24: wrapped in a collapsible "Add links (optional)"
                      disclosure. Chips always visible (via disabled-mode
                      EntityLinksMultiField); only the picker collapses. */}
                  {!editingAgenda && (
                    <div className="meeting-detail-form-grid__full">
                      <div className="meeting-detail-link-disclosure">
                        <button
                          type="button"
                          className="meeting-detail-link-disclosure__toggle"
                          onClick={() => setNewAgendaLinksOpen((o) => !o)}
                          aria-expanded={newAgendaLinksOpen}
                          data-testid="BTN_meeting_detail_agenda_links_toggle"
                        >
                          {newAgendaLinks.length === 0 ? (
                            <>
                              <Plus size={12} />
                              <span>Add links (optional)</span>
                            </>
                          ) : (
                            <>
                              <LinkIcon size={12} />
                              <span>Linked items ({newAgendaLinks.length})</span>
                            </>
                          )}
                          {newAgendaLinksOpen ? (
                            <ChevronUp size={12} />
                          ) : (
                            <ChevronDown size={12} />
                          )}
                        </button>
                        {/* Chip-only view when collapsed — keeps the user aware
                            of what's already linked without opening the picker. */}
                        {!newAgendaLinksOpen && newAgendaLinks.length > 0 && (
                          <div className="meeting-detail-link-disclosure__chips">
                            <EntityLinksMultiField
                              value={newAgendaLinks}
                              onChange={setNewAgendaLinks}
                              allowedTypes={linkableTypeCodes}
                              disabled
                            />
                          </div>
                        )}
                        {newAgendaLinksOpen && (
                          <div className="meeting-detail-link-disclosure__body">
                            <EntityLinksMultiField
                              value={newAgendaLinks}
                              onChange={setNewAgendaLinks}
                              allowedTypes={linkableTypeCodes}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="meeting-detail-form-actions">
                  <Button
                    variant="outline"
                    onClick={() => setShowAgendaForm(false)}
                    data-testid="BTN_meeting_detail_agenda_cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant={editingAgenda ? "update" : "fill"}
                    icon={editingAgenda ? Check : Save}
                    onClick={handleSaveAgenda}
                    disabled={submitting}
                    data-testid="BTN_meeting_detail_agenda_submit"
                  >
                    {submitting
                      ? editingAgenda
                        ? "Updating..."
                        : "Submitting..."
                      : editingAgenda
                        ? "Update"
                        : "Submit"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="meeting-detail-section-header">
                  <h3 className="meeting-detail-section-title">Agenda Items</h3>
                  {canEdit && (
                    <Button variant="fill" icon={Plus} onClick={handleAddAgenda} data-testid="BTN_meeting_detail_agenda_create">
                      Create
                    </Button>
                  )}
                </div>

                {agendaLoading ? (
                  <div className="flex flex-col gap-2" style={{ padding: '4px 0' }}>
                    {[1, 2, 3].map(i => (
                      <div key={i} className="collapse-section--l1 cs-closed">
                        <div className="collapse-section__header" style={{ pointerEvents: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                            <Skeleton.Box width={24} height={24} radius={6} />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                              <Skeleton.Box width="55%" height={12} />
                              <Skeleton.Box width="30%" height={10} />
                            </div>
                          </div>
                          <Skeleton.Pill width={60} height={20} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : agendaItems.length === 0 ? (
                  <div className="meeting-detail-empty">
                    <span>No agenda items yet.</span>
                    {canEdit && (
                      <Button variant="fill" icon={Plus} onClick={handleAddAgenda} data-testid="BTN_meeting_detail_agenda_create_empty">
                        Create
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="collapse-section-stack">
                    {agendaItems.map((item, idx) => {
                      const isExpanded = expandedAgenda === item.id;
                      const agendaStatusClass = item.agenda_status === "PASSED"
                        ? "success"
                        : item.agenda_status === "NOT_PASSED"
                          ? "danger"
                          : "warning";
                      return (
                        <div
                          key={item.id}
                          className={`collapse-section collapse-section--l1 ${isExpanded ? "cs-open" : "cs-closed"}`}
                        >
                          {/* Header row */}
                          <div
                            className="collapse-section__header"
                            onClick={() => handleToggleAgendaExpand(item.id)}
                            data-testid={`BTN_meeting_detail_agenda_row_${item.id}_toggle`}
                          >
                            <span className="collapse-section__toggle">
                              <ChevronDown size={14} />
                            </span>

                            <div className="collapse-section__title-block collapse-section__title-block--inline">
                              <span className="collapse-section__code">#{idx + 1}</span>
                              <span className="collapse-section__name">
                                {item.agenda_name}
                              </span>
                            </div>

                            {/* Header meta kept intentionally minimal: only the
                                vote aggregate + the pass/fail result. Code, type,
                                duration, presenter and discussed/resolved flags
                                live in the "1. Agenda" section inside the body. */}
                            <div className="collapse-section__meta">
                              {(() => {
                                const isBinary = meeting.vote_model === "BINARY";
                                const pos = isBinary ? (item.votes_upvote || 0) : (item.votes_for || 0);
                                const neg = isBinary ? (item.votes_downvote || 0) : (item.votes_against || 0);
                                const abs = isBinary ? 0 : (item.votes_abstain || 0);
                                return (
                                  <span className="meeting-agenda-head-tally" title="Votes">
                                    <span className="status-badge sm success">
                                      <ThumbsUp size={10} /> {pos}
                                    </span>
                                    <span className="status-badge sm danger">
                                      <ThumbsDown size={10} /> {neg}
                                    </span>
                                    {!isBinary && (
                                      <span className="status-badge sm neutral">
                                        <MinusCircle size={10} /> {abs}
                                      </span>
                                    )}
                                  </span>
                                );
                              })()}
                              {item.agenda_status && (
                                <span className={`status-badge sm ${agendaStatusClass}`}>
                                  {item.agenda_status.replace("_", " ")}
                                </span>
                              )}
                            </div>

                            {canEdit && (
                              <span style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleEditAgenda(item); }}
                                  className="tree-list-action tree-list-action--edit"
                                  title="Edit"
                                  data-testid={`BTN_meeting_detail_agenda_${item.id}_edit`}
                                >
                                  <Edit2 size={13} />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteAgenda(item.id); }}
                                  className="tree-list-action tree-list-action--delete"
                                  title="Delete"
                                  data-testid={`BTN_meeting_detail_agenda_${item.id}_delete`}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </span>
                            )}
                          </div>

                          {/* Expanded body */}
                          {isExpanded && (
                            <div className="collapse-section__body">

                              {/* ── 1. Agenda — the item's own details (code,
                                  type, timing, presenter, flags, description,
                                  resolution). Everything the header bar used to
                                  carry now lives here. ── */}
                              <div className="meeting-agenda-flat">
                                <div className="meeting-agenda-flat__head">
                                  <FileText size={13} />
                                  <span className="meeting-agenda-flat__title">1. Agenda</span>
                                </div>
                                <div className="meeting-agenda-flat__body">
                                  <div className="meeting-agenda-detail-meta">
                                    <span className="meeting-agenda-detail-meta__item">
                                      <span className="meeting-agenda-detail-meta__label">Code</span>
                                      <span data-testid={`TXT_agenda_items_row_${item.id}_code`}>{item.code || "—"}</span>
                                    </span>
                                    {item.agenda_type && (
                                      <span className="meeting-agenda-detail-meta__item">
                                        <span className="meeting-agenda-detail-meta__label">Type</span>
                                        <span>{item.agenda_type}</span>
                                      </span>
                                    )}
                                    {item.duration_minutes != null && (
                                      <span className="meeting-agenda-detail-meta__item">
                                        <span className="meeting-agenda-detail-meta__label">Duration</span>
                                        <span>{item.duration_minutes}m</span>
                                      </span>
                                    )}
                                    {item.presenter && (
                                      <span className="meeting-agenda-detail-meta__item">
                                        <span className="meeting-agenda-detail-meta__label">Presenter</span>
                                        <span>{item.presenter}</span>
                                      </span>
                                    )}
                                    {item.is_discussed && (
                                      <span className="meeting-agenda-detail-meta__item" style={{ color: '#16a34a' }}>
                                        <CheckCircle size={12} /> Discussed
                                      </span>
                                    )}
                                    {item.is_resolved && (
                                      <span className="meeting-agenda-detail-meta__item" style={{ color: '#2563eb' }}>
                                        <XCircle size={12} /> Resolved
                                      </span>
                                    )}
                                  </div>
                                  {item.agenda_description && (
                                    <p className="meeting-agenda-detail-desc">
                                      {item.agenda_description}
                                    </p>
                                  )}
                                  {item.resolution_text && (
                                    <p className="meeting-agenda-detail-desc">
                                      <strong>Resolution:</strong> {item.resolution_text}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* ── 2. Reference (flat — no accordion) ── */}
                              {(() => {
                                const linkRows = (agendaLinks[item.id] || []).map(
                                  (l) => ({
                                    id: l.id,
                                    type_id: l.item_type_id,
                                    type_code: l.item_type_code ?? null,
                                    record_id: l.item_record_id,
                                    label: l.item_label ?? null,
                                    code: l.item_code ?? null,
                                    notes: l.notes ?? null,
                                  }),
                                );
                                // Hide entirely when there is nothing to show and
                                // the viewer can't add links anyway.
                                if (linkRows.length === 0 && !canEdit) return null;
                                return (
                                  <div
                                    className="meeting-agenda-flat"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="meeting-agenda-flat__head">
                                      <LinkIcon size={13} />
                                      <span className="meeting-agenda-flat__title">2. Reference</span>
                                      <span className="meeting-agenda-flat__count">{linkRows.length}</span>
                                    </div>
                                    <div className="meeting-agenda-flat__body">
                                      <EntityLinksMultiField
                                        value={linkRows}
                                        onChange={(next) => handleAgendaLinksChange(item.id, next)}
                                        allowedTypes={linkableTypeCodes}
                                        disabled={!canEdit}
                                      />
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* ── 3. ToDo (flat — no accordion) ── */}
                              {(canCreateTaskForAgenda(item) || (agendaTasks[item.id]?.length ?? 0) > 0) && (() => {
                                const taskCount = agendaTasks[item.id]?.length ?? 0;
                                return (
                                  <div
                                    className="meeting-agenda-flat"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="meeting-agenda-flat__head">
                                      <CheckSquare size={13} />
                                      <span className="meeting-agenda-flat__title">3. ToDo</span>
                                      <span className="meeting-agenda-flat__count">{taskCount}</span>
                                      {canCreateTaskForAgenda(item) && (
                                        <span className="meeting-agenda-flat__head-action">
                                          <Button
                                            variant={item.agenda_status === "PASSED" ? "fill" : "outline"}
                                            icon={Plus}
                                            onClick={() => handleNewTaskFromAgenda(item)}
                                            data-testid={`BTN_meeting_detail_agenda_${item.id}_task_create`}
                                          >
                                            {taskCount === 0 ? "Add task" : "Create Task"}
                                          </Button>
                                        </span>
                                      )}
                                    </div>
                                    <div className="meeting-agenda-flat__body">
                                      {taskCount > 0 ? (
                                        <TaskList
                                          tasks={agendaTasks[item.id] || []}
                                          emptyText="No tasks yet from this agenda."
                                          hideSource
                                          onEdit={canEdit ? handleTaskEdit : undefined}
                                          onDelete={canEdit ? handleTaskDelete : undefined}
                                        />
                                      ) : (
                                        <p className="collapse-section__empty">No tasks yet from this agenda.</p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* ── 4. Voting (Participants) — all participants,
                                  with the logged-in user able to up/down-vote
                                  and add a remark against their own row. ── */}
                              {(() => {
                                const votes = agendaVotes[item.id] || [];
                                const voteByUser = new Map(
                                  votes.map((v) => [v.user_id, v]),
                                );
                                const isBinary = meeting.vote_model === "BINARY";
                                const posVal = isBinary ? "UPVOTE" : "FOR";
                                const negVal = isBinary ? "DOWNVOTE" : "AGAINST";
                                const myVote = currentUserVoteFor(item.id);
                                const voteDisabled = !canVote;
                                const disabledHint = isClosed || isCancelled
                                  ? "Voting closed."
                                  : isParticipant && !attended
                                    ? "Mark yourself as attending (button in the header) to vote."
                                    : "Only participants can vote.";

                                const voteWord = (val?: string | null) => {
                                  if (val === "UPVOTE" || val === "FOR") return "In favour";
                                  if (val === "DOWNVOTE" || val === "AGAINST") return "Against";
                                  if (val === "ABSTAIN") return "Abstained";
                                  return null;
                                };
                                const voteClass = (val?: string | null) =>
                                  val === "UPVOTE" || val === "FOR" ? "success"
                                    : val === "DOWNVOTE" || val === "AGAINST" ? "danger"
                                      : val === "ABSTAIN" ? "neutral" : "neutral";

                                return (
                                  <div
                                    className="meeting-agenda-flat"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="meeting-agenda-flat__head">
                                      <Users size={13} />
                                      <span className="meeting-agenda-flat__title">4. Voting (Participants)</span>
                                      <span className="meeting-agenda-flat__count">{votes.length}/{participants.length}</span>
                                    </div>
                                    <div className="meeting-agenda-flat__body">
                                      <div className="table-list">
                                        {/* Column headers omitted — Participant /
                                            Vote / Remarks are self-evident from
                                            the row content. */}
                                        <table className="table-list-table meeting-agenda-vote-table">
                                          <tbody>
                                            {participants.length === 0 && (
                                              <tr className="table-list-row" style={{ cursor: "default" }}>
                                                <td className="table-list-cell" colSpan={4}>
                                                  <span className="collapse-section__empty">No participants yet.</span>
                                                </td>
                                              </tr>
                                            )}
                                            {participants.map((p, pIdx) => {
                                              const v = voteByUser.get(p.user_id);
                                              const isMe = p.user_id === currentUserId;
                                              const name = p.user_name || p.login_id || `User #${p.user_id}`;
                                              const canVoteHere = isMe && viewMode !== "admin";
                                              return (
                                                <tr
                                                  key={p.id}
                                                  className={`table-list-row${isMe ? " meeting-agenda-vote-row--me" : ""}`}
                                                  style={{ cursor: "default" }}
                                                >
                                                  {/* S.No */}
                                                  <td className="table-list-cell meeting-agenda-vote-sno">
                                                    {pIdx + 1}
                                                  </td>
                                                  {/* Participant */}
                                                  <td className="table-list-cell">
                                                    <span className="table-list-cell-title">
                                                      {name}{isMe && <span className="meeting-agenda-vote-you"> (you)</span>}
                                                    </span>
                                                  </td>
                                                  {/* Remarks — always shown as read-only text
                                                      (entered via the vote modal). */}
                                                  <td className="table-list-cell meeting-agenda-vote-remark-cell">
                                                    {v?.remarks
                                                      ? v.remarks
                                                      : <span className="collapse-section__empty">—</span>}
                                                  </td>
                                                  {/* Vote — horizontal up/down icons at the right
                                                      edge for the logged-in participant (each opens
                                                      the remark modal); a recorded-vote icon for
                                                      everyone else. */}
                                                  <td className="table-list-cell meeting-agenda-vote-cell">
                                                    {canVoteHere ? (
                                                      <div className="meeting-agenda-vote-btns">
                                                        <button
                                                          type="button"
                                                          className={`meeting-agenda-vote-icon meeting-agenda-vote-icon--up${myVote === posVal ? " active" : ""}`}
                                                          disabled={voteDisabled}
                                                          title={isBinary ? "Upvote" : "For"}
                                                          aria-label={isBinary ? "Upvote" : "For"}
                                                          onClick={(e) => { e.stopPropagation(); openVoteModal(item, posVal as any); }}
                                                          data-testid={`BTN_meeting_detail_agenda_${item.id}_vote_${isBinary ? "upvote" : "for"}`}
                                                        >
                                                          <ThumbsUp size={16} />
                                                        </button>
                                                        <button
                                                          type="button"
                                                          className={`meeting-agenda-vote-icon meeting-agenda-vote-icon--down${myVote === negVal ? " active" : ""}`}
                                                          disabled={voteDisabled}
                                                          title={isBinary ? "Downvote" : "Against"}
                                                          aria-label={isBinary ? "Downvote" : "Against"}
                                                          onClick={(e) => { e.stopPropagation(); openVoteModal(item, negVal as any); }}
                                                          data-testid={`BTN_meeting_detail_agenda_${item.id}_vote_${isBinary ? "downvote" : "against"}`}
                                                        >
                                                          <ThumbsDown size={16} />
                                                        </button>
                                                        {!isBinary && (
                                                          <button
                                                            type="button"
                                                            className={`meeting-agenda-vote-icon meeting-agenda-vote-icon--abstain${myVote === "ABSTAIN" ? " active" : ""}`}
                                                            disabled={voteDisabled}
                                                            title="Abstain"
                                                            aria-label="Abstain"
                                                            onClick={(e) => { e.stopPropagation(); openVoteModal(item, "ABSTAIN"); }}
                                                            data-testid={`BTN_meeting_detail_agenda_${item.id}_vote_abstain`}
                                                          >
                                                            <MinusCircle size={16} />
                                                          </button>
                                                        )}
                                                      </div>
                                                    ) : voteWord(v?.vote) ? (
                                                      <span className={`status-badge xs ${voteClass(v?.vote)}`} title={voteWord(v?.vote) || undefined}>
                                                        {v?.vote === "UPVOTE" || v?.vote === "FOR"
                                                          ? <ThumbsUp size={12} />
                                                          : v?.vote === "DOWNVOTE" || v?.vote === "AGAINST"
                                                            ? <ThumbsDown size={12} />
                                                            : <MinusCircle size={12} />}
                                                      </span>
                                                    ) : (
                                                      <span className="collapse-section__empty">Not voted</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                      {voteDisabled && viewMode !== "admin" && (
                                        <p className="collapse-section__empty" style={{ marginTop: 6 }}>
                                          {disabledHint}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* === PARTICIPANTS TAB === */}
        {activeTab === "participants" && (
          <div>
            <div className="meeting-detail-section-header">
              <h3 className="meeting-detail-section-title">
                Participants ({participants.length})
              </h3>
              {canEdit && (
                <Button
                  variant="fill"
                  icon={Plus}
                  onClick={() => setShowUserPicker(!showUserPicker)}
                  data-testid="BTN_meeting_detail_participant_create"
                >
                  Create
                </Button>
              )}
            </div>

            {/* User picker */}
            {showUserPicker && canEdit && (
              <div className="meeting-detail-inline-panel">
                <FormControl.Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  leadingIcon={<Search size={14} />}
                  data-testid="INP_meeting_detail_participant_search"
                />
                <div className="meeting-detail-user-picker-list">
                  {availableUsers.length === 0 ? (
                    <div className="meeting-detail-user-picker-empty">
                      No users found
                    </div>
                  ) : (
                    availableUsers.map((u) => (
                      <div key={u.id} className="meeting-detail-user-picker-row">
                        <div>
                          <span className="meeting-detail-user-picker-row__name">
                            {u.user_name}
                          </span>
                          <span className="meeting-detail-user-picker-row__login">
                            ({u.login_id})
                          </span>
                        </div>
                        <Button
                          variant="fill"
                          onClick={() => handleAddParticipant(u.id)}
                          data-testid={`BTN_meeting_detail_participant_add_${u.id}`}
                        >
                          Add
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Participant list */}
            {participantsLoading ? (
              <div className="meeting-detail-loading">
                Loading participants...
              </div>
            ) : participants.length === 0 ? (
              <div className="meeting-detail-loading">
                No participants added yet.
              </div>
            ) : (
              <div className="data-table-container">
                <div className="data-table-header meeting-detail-participant-grid">
                  <div className="header-cell">Name</div>
                  <div className="header-cell">Position</div>
                  <div className="header-cell">Status</div>
                  <div className="header-cell">Actions</div>
                </div>
                <div className="data-table-body">
                  {participants.map((p) => (
                    <div
                      key={p.id}
                      className="data-table-row meeting-detail-participant-grid"
                    >
                      <div className="data-table-cell">
                        <div className="meeting-detail-participant-name">
                          {p.user_name || p.login_id || "Unknown"}
                        </div>
                      </div>
                      <div className="data-table-cell meeting-detail-participant-position">
                        {p.position_name || "—"}
                      </div>
                      <div className="data-table-cell">
                        {/* v3 attendance pills — visible to admin + chair as
                            read-only display. The chair/admin needs to see at
                            a glance who's actually in the room (this drives
                            quorum on MAJORITY_WITH_QUORUM meetings). */}
                        {p.has_attended ? (
                          <span
                            className={`${statusBadgeCss('active')} meeting-detail-attendance-pill`}
                            title={
                              p.attended_from_time
                                ? `Attended from ${formatTime(p.attended_from_time)}`
                                : "Attended"
                            }
                          >
                            <Check size={10} /> Attended
                            {p.attended_from_time && (
                              <span className="meeting-detail-attendance-pill__time">
                                {formatTime(p.attended_from_time)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span
                            className={`${statusBadgeCss('inactive')} meeting-detail-attendance-pill`}
                            title={
                              p.is_scheduled_participant
                                ? "Scheduled but not yet attended"
                                : "Invited"
                            }
                          >
                            Not attended
                          </span>
                        )}
                      </div>
                      <div className="data-table-cell">
                        {canEdit && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveParticipant(p.id);
                            }}
                            className="tree-list-action tree-list-action--delete"
                            title="Remove"
                            data-testid={`BTN_meeting_detail_participant_${p.id}_remove`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === TASKS TAB === */}
        {activeTab === "tasks" && (
          <div>
            <div className="meeting-detail-section-header">
              <h3 className="meeting-detail-section-title">
                All Tasks
                <span className="meeting-detail-section-subtitle">
                  — across this meeting and its agenda items
                </span>
              </h3>
              {canEdit && (
                <Button
                  variant="fill"
                  icon={Plus}
                  onClick={handleNewTaskFromMeeting}
                  data-testid="BTN_meeting_detail_task_create"
                >
                  Create
                </Button>
              )}
            </div>

            {/* v2 R29: TaskForm slot removed — replaced by the nested
                popup Modal at the root of this component. */}

            <TaskList
              tasks={meetingTasks}
              loading={meetingTasksLoading}
              emptyText="No tasks yet for this meeting. Add tasks from individual agenda items, or use 'New Task' to add a meeting-level one."
              onEdit={canEdit ? handleTaskEdit : undefined}
              onDelete={canEdit ? handleTaskDelete : undefined}
            />
          </div>
        )}

        {/* === EDIT TAB === */}
        {activeTab === "edit" && canEdit && (
          <CreateMeetingWizard
            mode="edit"
            isOpen={activeTab === "edit"}
            inline
            footerSlot={wizardFooterSlot}
            meeting={meeting}
            onClose={() =>
              setActiveTab(viewMode === "admin" ? "edit" : "participants")
            }
            onSaved={(saved) => {
              setMeetingState(saved);
              onMeetingUpdated();
              // GRC Team (admin) has no tab bar — stay on the wizard after save
              // instead of stranding them on a tab-less participants view.
              setActiveTab(viewMode === "admin" ? "edit" : "participants");
            }}
          />
        )}

        {/* === MINUTES TAB === */}
        {activeTab === "minutes" && (
          <div>
            <h3 className="meeting-detail-section-title meeting-detail-section-title--standalone">
              Minutes of Meeting
            </h3>

            {/* === YOUR (manual) MINUTES (migration 159) ===
                Editable Markdown for the chair / attended participants, rendered
                live via react-markdown + remark-gfm (same renderer as the auto-
                gen). Locked to a read-only render once the chair finalises. */}
            {canEditMinutes ? (
              <div className="meeting-detail-manual-minutes">
                <div className="meeting-detail-manual-minutes__toolbar">
                  {/* WYSIWYG edits the formatted document directly, so there is
                      no raw-markdown "Edit" view to toggle to — the editor IS
                      the formatted view. Generate-from-agenda stays. */}
                  <Button
                    variant="outline"
                    onClick={handleGenerateAutoMinutes}
                    disabled={generatingMinutes}
                    data-testid="BTN_meeting_minutes_generate"
                  >
                    {generatingMinutes ? "Generating…" : "Generate from agenda"}
                  </Button>
                </div>

                <MarkdownEditor
                  value={manualMinutesDraft}
                  onChange={setManualMinutesDraft}
                  placeholder="Write the minutes here. Use the toolbar for headings, bold, lists and tables."
                  data-testid="TXT_meeting_manual_minutes"
                />

                <div className="meeting-detail-manual-minutes__actions">
                  <Button
                    variant="fill"
                    icon={Save}
                    onClick={handleSaveMinutes}
                    disabled={savingMinutes}
                    data-testid="BTN_meeting_minutes_save"
                  >
                    {savingMinutes ? "Saving..." : "Save"}
                  </Button>
                  {minutesJustSaved && (
                    <span
                      className="meeting-detail-minutes-saved-hint"
                      data-testid="TXT_meeting_minutes_saved"
                      style={{ display: "inline-flex", alignItems: "center", gap: 4,
                        color: "#15803d", fontSize: 13, fontWeight: 600 }}
                    >
                      <Check size={14} /> Saved
                    </span>
                  )}
                  {canFinalizeMinutes && (
                    <Button
                      variant="outline"
                      icon={Check}
                      onClick={handleFinalizeMinutes}
                      disabled={savingMinutes}
                      data-testid="BTN_meeting_minutes_finalize"
                    >
                      Finalize
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              /* Read-only: minutes finalised, or the viewer has no edit rights. */
              <div>
                {meeting.manual_minutes_text &&
                meeting.manual_minutes_text.trim() ? (
                  <div className="meeting-minutes-render meeting-detail-minutes-render">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {meeting.manual_minutes_text}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="meeting-detail-minutes-placeholder">
                    No minutes written yet.
                  </div>
                )}
                {/* Staff persona: a scheduled participant who hasn't opted in
                    can't edit minutes yet — say WHY the editor is missing
                    instead of silently rendering read-only. */}
                {!readOnly && !minutesFinalized && viewMode === "participant" &&
                  isParticipant && !myAttended && !isClosed && !isCancelled && (
                  <div
                    className="meeting-detail-minutes-placeholder"
                    data-testid="TXT_meeting_minutes_attend_hint"
                  >
                    Mark yourself as attending (button in the header above) to
                    write or edit the minutes.
                  </div>
                )}
              </div>
            )}

            {/* Finalised banner + chair-only Reopen. */}
            {minutesFinalized && (
              <div className="meeting-detail-minutes-banner">
                <Check size={14} /> Minutes finalized
                {minutesFinalizerName && <span> by {minutesFinalizerName}</span>}
                {meeting.manual_minutes_finalized_on && (
                  <span>
                    {" "}
                    on{" "}
                    {formatDateTime(meeting.manual_minutes_finalized_on)}
                  </span>
                )}
                {canUnfinalizeMinutes && (
                  <Button
                    variant="outline"
                    onClick={handleUnfinalizeMinutes}
                    disabled={savingMinutes}
                    data-testid="BTN_meeting_minutes_unfinalize"
                  >
                    Reopen
                  </Button>
                )}
              </div>
            )}

            {/* === Auto-generated summary (collapsible reference) ===
                The server-composed minutes_text stays available as a reference
                under the human-written record. Only exists once closed. */}
            {isClosed && meeting.minutes_text && (
              <div className="meeting-detail-minutes-autogen">
                <button
                  type="button"
                  className="meeting-detail-minutes-autogen__toggle"
                  onClick={() => setAutoGenOpen((o) => !o)}
                  data-testid="BTN_meeting_minutes_autogen_toggle"
                >
                  {autoGenOpen ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                  Auto-generated summary (reference)
                </button>
                {autoGenOpen && (
                  <div className="meeting-minutes-render meeting-detail-minutes-render">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {meeting.minutes_text}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            )}

            {isClosed && (
              <div className="meeting-detail-minutes-banner">
                This meeting has been closed.
                {meeting.closed_on && (
                  <span>
                    {" "}
                    Closed on: {formatDateTime(meeting.closed_on)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      {/* Real modal footer for the inline edit wizard. The wizard portals its
          Back / Cancel / Next / Update action row into this slot so those
          buttons live in the modal's fixed footer bar (consistent with every
          other modal) instead of scrolling inside the body. Only mounted on
          the edit tab where the wizard renders. */}
      {activeTab === "edit" && canEdit && (
        <Modal.Footer>
          <div
            ref={setWizardFooterSlot}
            className="create-meeting-wizard__footer-slot"
          />
        </Modal.Footer>
      )}

      {/* v2 R29: TaskForm always opens in a nested popup Modal — never
          inline under an agenda row. Works for every viewMode (admin /
          chair / participant) because TaskForm itself handles save/cancel
          and we keep handleTaskSaved / handleTaskCancel here so the
          underlying meeting modal refreshes its agenda task counts +
          overview header on save. The portal-rendered Modal stacks on
          top of the parent automatically via its overlay z-index. */}
      {showTaskForm && (
        <Modal
          isOpen={showTaskForm}
          onClose={() => {
            setShowTaskForm(false);
            setTaskFormForAgendaId(null);
            setEditingTask(null);
            setTaskFormSeed(null);
          }}
          size="lg"
        >
          <Modal.Header>
            {editingTask ? "Edit Task" : "Create Task"}
          </Modal.Header>
          <Modal.Body>
            <TaskForm
              formId="meeting-task-form"
              sourceTypeId={
                taskFormForAgendaId !== null
                  ? agendaTypeId ?? undefined
                  : meetingTypeId ?? undefined
              }
              sourceRecordId={taskFormForAgendaId ?? meeting.id}
              initial={editingTask || taskFormSeed || undefined}
              onSaved={handleTaskSaved}
              onCancel={() => {
                setShowTaskForm(false);
                setTaskFormForAgendaId(null);
                setEditingTask(null);
                setTaskFormSeed(null);
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant={editingTask ? "update" : "fill"}
              icon={editingTask ? Check : Save}
              type="submit"
              form="meeting-task-form"
              data-testid="BTN_meeting_detail_task_submit"
            >
              {editingTask ? "Update" : "Submit"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Vote-with-remark modal — opens on up/down/abstain click. The remark is
          optional; confirming casts the vote (with the remark) in one call. */}
      {voteModal && (() => {
        const isBinary = meeting.vote_model === "BINARY";
        const label =
          voteModal.vote === "UPVOTE" || voteModal.vote === "FOR"
            ? (isBinary ? "Upvote" : "For")
            : voteModal.vote === "DOWNVOTE" || voteModal.vote === "AGAINST"
              ? (isBinary ? "Downvote" : "Against")
              : "Abstain";
        return (
          <Modal isOpen={!!voteModal} onClose={() => setVoteModal(null)} size="sm">
            <Modal.Header>Confirm vote</Modal.Header>
            <Modal.Body>
              <p className="meeting-vote-modal-line">
                You are voting{" "}
                <strong className={
                  voteModal.vote === "UPVOTE" || voteModal.vote === "FOR"
                    ? "meeting-vote-modal-label--up"
                    : voteModal.vote === "DOWNVOTE" || voteModal.vote === "AGAINST"
                      ? "meeting-vote-modal-label--down"
                      : "meeting-vote-modal-label--abstain"
                }>{label}</strong>{" "}
                on <strong>{voteModal.agendaName}</strong>.
              </p>
              <FormControl.Textarea
                label="Remark (optional)"
                rows={4}
                value={voteModal.remark}
                onChange={(e) =>
                  setVoteModal((prev) => prev ? { ...prev, remark: e.target.value } : prev)
                }
                placeholder="Add a note explaining your vote…"
                data-testid="TXT_meeting_vote_remark"
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline"
                onClick={() => setVoteModal(null)}
                data-testid="BTN_meeting_vote_cancel"
              >
                Cancel
              </Button>
              <Button
                variant="fill"
                icon={Check}
                disabled={voteSubmitting}
                onClick={submitVoteModal}
                data-testid="BTN_meeting_vote_confirm"
              >
                {voteSubmitting ? "Saving…" : `Confirm ${label}`}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      })()}
    </Modal>
  );
};

export default MeetingDetailModal;
